import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import motivoDescontoService, { MotivoDesconto } from '../../services/motivoDescontoService';

interface MotivoDescontoFormProps {
  open: boolean;
  onClose: () => void;
  motivoDesconto?: MotivoDesconto | null;
  onSuccess?: (motivo: MotivoDesconto) => void;
  onError?: (error: string) => void;
}

const MotivoDescontoForm: React.FC<MotivoDescontoFormProps> = ({
  open,
  onClose,
  motivoDesconto,
  onSuccess,
  onError
}) => {
  const [formData, setFormData] = useState({
    descricao: '',
    ativo: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      if (motivoDesconto) {
        setFormData({
          descricao: motivoDesconto.descricao,
          ativo: motivoDesconto.ativo
        });
      } else {
        setFormData({
          descricao: '',
          ativo: true
        });
      }
      setErrors([]);
    }
  }, [open, motivoDesconto]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];
    if (!formData.descricao || formData.descricao.trim().length < 2) {
      newErrors.push('Descrição deve ter pelo menos 2 caracteres');
    }
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      if (motivoDesconto) {
        const updated = await motivoDescontoService.updateMotivo(motivoDesconto.id, formData);
        onSuccess?.(updated);
      } else {
        const created = await motivoDescontoService.createMotivo(formData);
        onSuccess?.(created);
      }
      onClose();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Erro ao salvar';
      onError?.(msg);
      setErrors([msg]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {motivoDesconto ? 'Editar Motivo de Desconto' : 'Novo Motivo de Desconto'}
          <Button size="small" onClick={onClose} startIcon={<Close />}>
            Fechar
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errors.length > 0 && (
            <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
              {errors.map((e, i) => <div key={i}>{e}</div>)}
            </Box>
          )}
          <TextField
            fullWidth
            label="Descrição *"
            value={formData.descricao}
            onChange={(e) => handleInputChange('descricao', e.target.value)}
            size="small"
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.ativo}
                onChange={(e) => handleInputChange('ativo', e.target.checked)}
                color="primary"
              />
            }
            label="Status (Ativo)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={<Save />}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MotivoDescontoForm;
